import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { check, maxValue, minValue, notValue, nullable, number, object, pipe, string, transform } from "valibot";
import { dataName, decimalValue, errorTranslation, label, rememberInput, required } from "../../utils/validation/base";
import { isNil } from "lodash-es";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { NumberInput } from "../../components/inputs/NumberInput";
import { MAX_NUMBER_XL, MAX_NUMBER_XL_NEGATIVE, NUMBER_CONDITION_KEYS } from "../../constants";
import { defaultNumberFilter } from "../../pages/customer/form/filter";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { PreviewContentLabel, SliderPreview } from "../FieldPreviewComponents";
import { createMemo } from "solid-js";
import GlobalErrorInlineMessage from "../GlobalErrorInlineMessage";
import { keyFromSelector } from "i18next";

const Schema = pipe(
  object({
    label_text: label(),
    data_name: dataName(),
    default_value: nullable(decimalValue(MAX_NUMBER_XL_NEGATIVE, MAX_NUMBER_XL)),
    min: decimalValue(MAX_NUMBER_XL_NEGATIVE, MAX_NUMBER_XL),
    max: decimalValue(MAX_NUMBER_XL_NEGATIVE, MAX_NUMBER_XL),
    step: pipe(
      number(errorTranslation(keyFromSelector(($) => $.VALIDATION_DECIMAL))),
      notValue(0, errorTranslation(keyFromSelector(($) => $.VALIDATION_MIN_SIZE))),
      minValue(0, errorTranslation(keyFromSelector(($) => $.VALIDATION_MIN_SIZE))),
      maxValue(MAX_NUMBER_XL, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_SIZE))),
    ),
    required: required(),
    remember_input: rememberInput(),
  }),
  check(
    ({ min, max }) => max >= min,
    errorTranslation(keyFromSelector(($) => $.VALIDATION_NUMERIC_MAX_GREATER_EQUALS_MIN)),
  ),
  check(
    ({ min, max, default_value }) => (isNil(default_value) ? true : default_value >= min && default_value <= max),
    errorTranslation(keyFromSelector(($) => $.VALIDATION_NUMBER_DEFAULT_OUTSIDE_MIN_MAX)),
  ),
  check(
    ({ min, max, step }) => (isNil(step) ? true : step <= Math.abs(min - max)),
    errorTranslation(keyFromSelector(($) => $.VALIDATION_STEP_OUTSIDE_MIN_MAX)),
  ),
);
const ValueSchema = pipe(string(), transform(Number.parseFloat));

export type SliderFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const SliderField: Field<SliderFieldProperties, DefaultDtoType, ValueType> = {
  type: "slider",
  category: "number",
  widget: "com.moreapps:slider:1",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    min: 0,
    max: 10,
    step: 1,
    default_value: null,
    required: false,
    remember_input: false,
  }),
  ruleBehaviour: {
    conditions: { keys: NUMBER_CONDITION_KEYS, variant: "text" },
    actions: {
      variant: "text",
      setValue: {
        fieldTypes: ["calculation", "lookup", "number", "radio", "rating", "search", "slider", "smiley", "video"],
        variableTypes: ["number"],
      },
    },
  },
  filter: {
    type: "number",
    definition: defaultNumberFilter,
  },
  stringValue: (value) => value.toString(),
  renderValue: (value, { fieldConfig }) => {
    const { t } = useTranslation();
    return t(($) => $.SLIDER_FIELD_VALUE_LABEL, { value, maxValue: fieldConfig.properties.max });
  },
  icon: "adjustments",
  titleKey: keyFromSelector(($) => $.SLIDER_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.SLIDER_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    default_value: keyFromSelector(($) => $.FIELD_PROPERTY_DEFAULT_VALUE),
    min: keyFromSelector(($) => $.FIELD_PROPERTY_NUMERIC_MIN),
    max: keyFromSelector(($) => $.FIELD_PROPERTY_NUMERIC_MAX),
    step: keyFromSelector(($) => $.FIELD_PROPERTY_STEP),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <SliderPreview
        defaultValue={props.properties.default_value}
        max={props.properties.max}
        min={props.properties.min}
      />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();
    const globalErrors = createMemo(() => props.getGlobalErrors());

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
          <NumberInput
            label={t(props.propertyKeys.default_value)}
            value={props.data.properties.default_value}
            minValue={MAX_NUMBER_XL_NEGATIVE}
            maxValue={MAX_NUMBER_XL}
            allowDecimal
            onBlur={(value) => props.setValue("default_value", value)}
            errorMessage={props.getError("default_value")}
          />

          <NumberInput
            label={t(props.propertyKeys.step)}
            value={props.data.properties.step}
            minValue={0}
            maxValue={MAX_NUMBER_XL}
            allowDecimal
            required
            onBlur={(value) => props.setValue("step", value)}
            errorMessage={props.getError("step")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
          <NumberInput
            label={t(props.propertyKeys.min)}
            value={props.data.properties.min}
            minValue={MAX_NUMBER_XL_NEGATIVE}
            maxValue={MAX_NUMBER_XL}
            allowDecimal
            required
            onBlur={(value) => props.setValue("min", value)}
            errorMessage={props.getError("min")}
          />
          <NumberInput
            label={t(props.propertyKeys.max)}
            value={props.data.properties.max}
            minValue={MAX_NUMBER_XL_NEGATIVE}
            maxValue={MAX_NUMBER_XL}
            allowDecimal
            required
            onBlur={(value) => props.setValue("max", value)}
            errorMessage={props.getError("max")}
          />
          <GlobalErrorInlineMessage errors={globalErrors()} />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(SliderField);
